import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, XCircle, Clock, AlertCircle, Loader2,
  UserCheck, UserX, Shield, ArrowLeft
} from 'lucide-react';
import './ApproveRegistration.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e5c77d79-ca13-402d-b554-25dcc110651b/artifacts/mj5brxa4_Logo%20Freiheitsmacher%20transparent.png';

const CardLogo = () => (
  <img src={LOGO_URL} alt="Die Freiheitsmacher" className="approve-logo" data-testid="approve-logo" />
);

function ApproveRegistration() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Kein Freigabe-Token gefunden');
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/community/approval/${token}`);
        setData(resp.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Fehler beim Laden der Freigabe-Details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [token]);

  const handleAction = async (action) => {
    setProcessing(true);
    setError('');
    try {
      const resp = await axios.post(`${API_URL}/api/community/approval/${token}`, {
        action,
      });
      setResult({ action, message: resp.data.message });

      // Cross-pod sync via iframes (authorized by the signed approval token)
      if (token) {
        if (action === 'rejected') {
          // For rejection: DELETE the user on all pods
          for (let i = 0; i < 8; i++) {
            setTimeout(() => {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = `${API_URL}/api/internal/sync-delete-ping?token=${encodeURIComponent(token)}&k=${Date.now()}_${i}`;
              document.body.appendChild(iframe);
              setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 5000);
            }, i * 400);
          }
          // Also POST delete calls
          for (let i = 0; i < 5; i++) {
            setTimeout(() => {
              fetch(`${API_URL}/api/internal/sync-delete-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
                cache: 'no-store',
              }).catch(() => {});
            }, i * 600);
          }
        } else {
          // For approval: sync status to "active"
          for (let i = 0; i < 8; i++) {
            setTimeout(() => {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = `${API_URL}/api/internal/sync-status-ping?token=${encodeURIComponent(token)}&user_status=active&k=${Date.now()}_${i}`;
              document.body.appendChild(iframe);
              setTimeout(() => { try { document.body.removeChild(iframe); } catch(e) {} }, 5000);
            }, i * 400);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="approve-page" data-testid="approve-loading">
        <div className="approve-container">
          <div className="approve-card">
            <CardLogo />
            <Loader2 size={36} className="spin approve-loader" />
            <p>Lade Freigabe-Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="approve-page" data-testid="approve-error">
        <div className="approve-container">
          <div className="approve-card">
            <CardLogo />
            <AlertCircle size={48} className="error-icon" />
            <h2>Fehler</h2>
            <p>{error}</p>
            <Link to="/" className="btn-back" data-testid="back-home-button">
              <ArrowLeft size={16} />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Already used
  if (data?.status === 'used') {
    return (
      <div className="approve-page" data-testid="approve-used">
        <div className="approve-container">
          <div className="approve-card">
            {data.action === 'approved' ? (
              <CheckCircle size={48} className="success-icon" />
            ) : (
              <XCircle size={48} className="error-icon" />
            )}
            <h2>Bereits bearbeitet</h2>
            <p>
              Diese Registrierung wurde bereits
              {data.action === 'approved' ? ' genehmigt' : ' abgelehnt'}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (data?.status === 'expired') {
    return (
      <div className="approve-page" data-testid="approve-expired">
        <div className="approve-container">
          <div className="approve-card">
            <CardLogo />
            <Clock size={48} className="warning-icon" />
            <h2>Link abgelaufen</h2>
            <p>Dieser Freigabe-Link ist abgelaufen. Bitte kontaktieren Sie den Administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  // Result after action
  if (result) {
    return (
      <div className="approve-page" data-testid="approve-result">
        <div className="approve-container">
          <div className="approve-card">
            <CardLogo />
            {result.action === 'approved' ? (
              <CheckCircle size={48} className="success-icon" />
            ) : (
              <XCircle size={48} className="error-icon" />
            )}
            <h2>{result.action === 'approved' ? 'Genehmigt!' : 'Abgelehnt'}</h2>
            <p>{result.message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Pending - show approve/reject form
  const user = data?.user;
  return (
    <div className="approve-page" data-testid="approve-pending">
      <div className="approve-container">
        <div className="approve-card">
          <div className="approve-header">
            <CardLogo />
            <Shield size={32} className="approve-shield" />
            <h2>Registrierung prüfen</h2>
            <p>Ein neuer Benutzer moechte sich in Ihrer Downline registrieren.</p>
          </div>

          {error && (
            <div className="form-error" data-testid="approve-action-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="user-details" data-testid="user-details">
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value" data-testid="user-name">
                {user?.first_name} {user?.last_name}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">E-Mail</span>
              <span className="detail-value" data-testid="user-email">{user?.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Benutzername</span>
              <span className="detail-value" data-testid="user-username">{user?.username}</span>
            </div>
          </div>

          <div className="approve-actions">
            <button
              onClick={() => handleAction('approved')}
              className="btn-approve"
              disabled={processing}
              data-testid="approve-button"
            >
              {processing ? (
                <Loader2 size={18} className="spin" />
              ) : (
                <UserCheck size={18} />
              )}
              Genehmigen
            </button>
            <button
              onClick={() => handleAction('rejected')}
              className="btn-reject"
              disabled={processing}
              data-testid="reject-button"
            >
              {processing ? (
                <Loader2 size={18} className="spin" />
              ) : (
                <UserX size={18} />
              )}
              Ablehnen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApproveRegistration;
